import type { OneOfElements } from '@shared/models/project.model';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { clearStaleDerivedQuantityCounts } from '../../auto-quantity.helpers';
import { isExpressionValue } from '@shared/helpers/expression-factory.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getElementQuantityRecord } from '../../element-quantity.helpers';
import { isElement } from '../../recursive-element.helpers';
import { resolvedCountAfterClearingOverride } from './element-quantity.update';

type QuantityCountChange =
  ProjectChangeMap[ProjectChangeType.ElementQuantityCount];

/**
 * Build a partial element change for {@link ProjectChangeType.ElementQuantityCount}.
 * Sets (or clears) a quantity's `count` so that {@link IElementQuantityExpressionProperty.fallbackCount}
 * takes effect when `count` is set to `undefined`.
 *
 * @param element - Current element state
 * @param change - Quantity count payload
 * @returns Partial element with updated quantity record, or `undefined` when the quantity is not found
 */
export const elementQuantityCountToChange = (
  element: OneOfElements,
  change: QuantityCountChange,
): Partial<OneOfElements> | undefined => {
  if (!isElement(element)) return undefined;

  const quantityRecord = getElementQuantityRecord(element);
  const quantity = quantityRecord[change.quantityName];
  if (!quantity) return undefined;

  const previousCount = isExpressionValue(quantity.count)
    ? quantity.count
    : undefined;
  const incomingCount = change.count;
  // Only re-solve when this write drops a manual override. Other count writes
  // already carry the pipeline's resolved number.
  const nextCount =
    isExpressionValue(incomingCount) &&
    incomingCount.value === undefined &&
    guards.is.finiteNumber(previousCount?.value)
      ? resolvedCountAfterClearingOverride(incomingCount)
      : incomingCount;
  const withCount = {
    ...quantityRecord,
    [change.quantityName]: { ...quantity, count: nextCount },
  };

  // Proposal/baseline apply must drop inverse formulas (e.g. area from mass) before
  // resolve/enrich; otherwise stale action rows override a fixed baseline (DEV-3332).
  return { quantity: clearStaleDerivedQuantityCounts(withCount) };
};
